import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import some from 'lodash/some';
import sum from 'lodash/sum';
import dayjs from '../../dayjs/dayjs_timezone';
import { confirmAvailability, createBooking, createReservation, rescheduleBooking } from '../services/api';
import { splitFullName } from '../utilities/stringUtility';
import { ordersActions } from '../reducers/orders';
import { uiActions } from '../reducers/ui';

export const {
  changeAdditionalDetails,
  changeCustomerDetails,
  onAddRepeatedFieldItems,
  onRemoveRepeatedFieldItems,
  onSetCustomerErrors,
  onSetDetailsErrors,
  onSetLocationErrors,
  onSetResourceErrors,
  removeTimeSlots,
  removeTimeSlotByIndex,
  setMultipleSlotsCount,
  setBookingFields,
  setCustomerDetails,
  setDateRange,
  setNewVariantOption,
  setOrderDetails,
  setRecurringMultipleSlots,
  setSelectedLocation,
  setSelectedResource,
  setSelectedTerm,
  setSelectedTimeslot,
  setSelectedVariant,
  setSelectedVariants,
  setTimeSlot,
} = ordersActions;

export const setVariantsQuantity = (name, quantity, { singleVariant = false, newVariantOption } = {}) => (dispatch, getState) => {
  const { product: { data } } = getState();
  const { order: { variants: orderVariants } } = getState();
  let variants = cloneDeep(orderVariants);
  const { settings: { setting: { name: widgetType } } } = getState();

  const [externalId, type] = name.split('_');

  if (singleVariant) variants = {};

  let variant = variants[externalId];

  if (variant) {
    variant[type] = +quantity || 0;
  } else {
    variant = find(data.variants, (x) => `${x.external_id}` === `${externalId}`);
    variants[externalId] = {
      ...variant,
      field: type,
      [type]: +quantity || 0,
    };
  }

  const totalQuantity = sum(map(variants, (_value, key) => variants[key].quantity * (variants[key].unit_multiplier || 1)));
  const totalPrice = sum(map(variants, (_value, key) => variants[key].quantity * variants[key].price));

  dispatch(setOrderDetails({
    variants,
    totalQuantity,
    totalPrice,
    newVariantOption,
    quantity,
  }));

  widgetType === 'Activity' ? dispatch(setSelectedVariants(externalId)) : dispatch(setSelectedVariant(+externalId));
};

export const resetVariantsQuantity = () => (dispatch, getState) => {
  const {
    product: { data },
    order: { variants: orderVariants, date },
  } = getState();
  const variants = cloneDeep(orderVariants);

  const dateObj = date ? dayjs(date).toDate() : null;

  const seasonalRates = filter(data.seasonal_rates, (seasonalRate) => {
    const srStart = dayjs(seasonalRate.starts_at).toDate();
    const srFinish = dayjs(seasonalRate.finishes_at).toDate();
    return dateObj && dateObj >= srStart && dateObj <= srFinish;
  });

  forEach(variants, (value, externalId) => {
    const isAvailableVariant = some(seasonalRates, (seasonalRate) => seasonalRate.season === variants[externalId].options[0]
      && seasonalRate.rate === variants[externalId].options[1]);
    if (!isAvailableVariant) variants[externalId] = { ...variants[externalId], quantity: 0 };
  });

  const totalQuantity = sum(map(variants, (_value, key) => variants[key].quantity));
  const totalPrice = sum(map(variants, (_value, key) => variants[key].quantity * variants[key].price));

  dispatch(setOrderDetails({
    variants,
    totalQuantity,
    totalPrice,
  }));
};

export const submitOrder = (cartItems = []) => (_dispatch, getState) => {
  const {
    order: {
      date, selectedTimeslot, variants, selectedLocation,
    },
    frames: { frames },
    settings: { reservationEnabled },
  } = getState();

  const frame = frames.find((x) => x.date === date);
  const selectedStart = typeof selectedTimeslot === 'object' ? selectedTimeslot.start : selectedTimeslot;
  const timeslot = frame.timeslots.find((x) => x.start === selectedStart);

  const items = filter(variants, (x) => !!x.quantity).map((value) => ({
    externalId: value.external_id,
    quantity: value.quantity,
    location_name: selectedLocation ? selectedLocation.name : null,
    start: timeslot.start,
  }));

  return reservationEnabled ? createReservation(items.concat(cartItems)) : confirmAvailability(items);
};

export const submitClassOrder = (cartItems = []) => (_dispatch, getState) => {
  const {
    order: {
      date, selectedTimeslot, variants, quantity = 1, selectedLocation,
    },
    frames: { frames },
    settings: { reservationEnabled },
  } = getState();

  const frame = frames.find((x) => x.date === date);
  const selectedStart = selectedTimeslot.start;
  const timeslot = frame.timeslots.find((x) => x.start === selectedStart);

  const items = filter(variants, (x) => !!x.quantity).map((value) => ({
    externalId: value.external_id,
    quantity,
    location_name: selectedLocation ? selectedLocation.name : null,
    start: timeslot.start,
  }));

  return reservationEnabled ? createReservation(items.concat(cartItems)) : confirmAvailability(items);
};

export const submitEventsListOrder = (cartItems = []) => (_dispatch, getState) => {
  const {
    order: {
      selectedTimeslot, variants, quantity = 1, selectedLocation,
    },
    frames: { frames },
    settings: { reservationEnabled },
  } = getState();

  const frame = frames.find((x) => x.date === selectedTimeslot.date);
  const selectedStart = selectedTimeslot.start;
  const timeslot = frame.timeslots.find((x) => x.start === selectedStart);

  const items = filter(variants, (x) => !!x.quantity).map((value) => ({
    externalId: value.external_id,
    quantity,
    location_name: selectedLocation ? selectedLocation.name : null,
    start: timeslot.start,
  }));

  return reservationEnabled ? createReservation(items.concat(cartItems)) : confirmAvailability(items);
};

export const submitCourseOrder = (cartItems = []) => (_dispatch, getState) => {
  const {
    order: {
      variantSelected, termSelected, quantity = 1, selectedLocation,
    },
    product: { data },
    settings: { reservationEnabled },
  } = getState();

  const { duration } = data;
  const startDate = termSelected.start_date;

  let finishDate = termSelected.finish_date;

  if (dayjs(startDate).format() !== dayjs(finishDate).subtract(duration, 'seconds').format()) {
    finishDate = dayjs.utc(finishDate).add(duration, 'seconds').format();
  }

  const items = [
    {
      externalId: variantSelected,
      quantity,
      location_name: selectedLocation ? selectedLocation.name : null,
      start: dayjs.utc(startDate).format(),
      finish: dayjs.utc(finishDate).format(),
    },
  ];

  return reservationEnabled ? createReservation(items.concat(cartItems)) : confirmAvailability(items);
};

export const createAppointment = () => (_dispatch, getState) => {
  const {
    order: {
      additionalDetails = {},
      customerDetails = {},
      variantSelected,
      selectedLocation,
      selectedResource,
      timeSlot,
      quantity = 1,
    },
    product: {
      data: {
        resources = [],
        locations = [],
      },
    },
    settings: {
      bookingFields = {},
    },
  } = getState();
  const { firstName, lastName } = splitFullName(customerDetails.name || '');
  let location = selectedLocation;

  const fields = [];
  Object.keys(additionalDetails).forEach((key) => {
    const bookingField = bookingFields.find(((field) => field.key === key));
    if (bookingField) {
      const name = bookingField.label;
      const values = additionalDetails[key];
      try {
        values.forEach((value) => fields.push({ name, value }));
      } catch {
        fields.push({ name, value: values });
      }
    }
  });

  const items = [];

  const buildItem = (q) => ({
    resource_id: selectedResource?.id,
    external_id: variantSelected,
    quantity: q,
  });

  if (resources.length > 1 && quantity > 1 && !selectedResource?.id) {
    for (let i = 0; i < quantity; i++) {
      items.push(buildItem(1));
    }
  } else {
    items.push(buildItem(quantity));
  }

  if (locations.length === 1 && !location?.id) {
    location = locations[0];
  }

  const params = {
    starts_at: timeSlot.timeSlot.start,
    finishes_at: timeSlot.timeSlot.finish,
    location_id: location?.id,
    source: 'via_standalone',
    items,
    customers: [
      {
        first_name: firstName,
        last_name: lastName,
        email: customerDetails.email,
        phone: customerDetails.phone,
      },
    ],
    fields,
  };

  return createBooking(params);
};

export const rescheduleBookingByToken = (params) => (dispatch, getState) => {
  const { settings: { bookingToken } } = getState();
  if (!bookingToken) return Promise.reject(new Error('No booking token provided'));

  dispatch(uiActions.addLoadingKey('reschedule'));

  return rescheduleBooking(bookingToken, params)
    .then((response) => {
      dispatch(uiActions.removeLoadingKey('reschedule'));
      return response;
    })
    .catch((error) => {
      dispatch(uiActions.removeLoadingKey('reschedule'));
      throw error;
    });
};

export const submitAppointment = (cartItems = []) => (_dispatch, getState) => {
  const {
    order: {
      variantSelected, selectedLocation, selectedResource, timeSlot, timeSlots, bookingFields, quantity = 1,
    },
    product: {
      data: {
        resources = [],
      },
    },
    settings: {
      multipleAppointmentsEnabled,
      reservationEnabled,
    },
  } = getState();

  const items = [];

  const buildItem = (q, index) => {
    const slot = typeof index === 'number' ? timeSlots[index] : timeSlot.timeSlot;
    return {
      externalId: variantSelected,
      quantity: q,
      resource_name: selectedResource ? selectedResource.name : null,
      location_name: selectedLocation ? selectedLocation.name : null,
      start: slot.start,
      finish: slot.finish,
      fields: bookingFields,
    };
  };

  const addItem = (index) => {
    if (resources.length > 1 && quantity > 1 && !selectedResource?.id) {
      for (let i = 0; i < quantity; i++) { items.push(buildItem(1, index)); }
    } else {
      items.push(buildItem(quantity, index));
    }
  };

  if (multipleAppointmentsEnabled && timeSlots.length) {
    for (let i = 0; i < timeSlots.length; i++) { addItem(i); }
  } else {
    addItem(null);
  }

  return reservationEnabled ? createReservation(cartItems.concat(items)) : confirmAvailability(items);
};

export const submitRentalOrder = (cartItems = []) => (_dispatch, getState) => {
  const {
    order: {
      variantSelected,
      dateRange,
      bookingFields,
      selectedLocation,
      quantity,
    },
    settings: { reservationEnabled },
  } = getState();

  const items = [
    {
      externalId: variantSelected,
      quantity,
      start: dateRange.start,
      finish: dateRange.finish,
      duration: dateRange.duration,
      fields: bookingFields,
      location_name: selectedLocation ? selectedLocation.name : null,
    },
  ];
  return reservationEnabled ? createReservation(items.concat(cartItems)) : confirmAvailability(items);
};
